import { useQuery } from '@tanstack/react-query';
import type { CountryCode, MyCashback } from '@capsico/contracts';
import { errorStatus } from '../api/client';
import { fetchCashback, fetchOrderCashback } from '../api/growth';

export const cashbackKey = ['cashback'] as const;

/** Growth off on the server (404) is not an error worth retrying. */
const retryUnless404 = (count: number, error: unknown) => errorStatus(error) !== 404 && count < 1;

/** The customer's cashback rules and history (W18). Null when signed out or switched off. */
export function useCashback(enabled: boolean) {
  const query = useQuery({
    queryKey: cashbackKey,
    queryFn: fetchCashback,
    enabled,
    staleTime: 5 * 60_000,
    retry: retryUnless404,
  });
  return { loading: query.isLoading, cashback: query.data ?? null };
}

/** Cashback a delivered order earned (shown on the order page). */
export function useOrderCashback(orderId: string, delivered: boolean) {
  const query = useQuery({
    queryKey: ['order-cashback', orderId],
    queryFn: () => fetchOrderCashback(orderId),
    enabled: delivered && orderId !== '',
    staleTime: 5 * 60_000,
    retry: retryUnless404,
  });
  return query.data ?? null;
}

/**
 * The "5% cashback on this order" estimate at checkout: only when the order is in the customer's
 * cashback country and its total reaches the minimum. Capped per order. The server decides the
 * real amount after delivery; this is only a hint.
 */
export function cashbackEstimate(
  cashback: MyCashback | null,
  country: CountryCode,
  totalMinor: number,
): { percent: number; amountMinor: number } | null {
  if (!cashback || cashback.country !== country || cashback.percent <= 0) return null;
  if (totalMinor < cashback.minOrderMinor) return null;
  const raw = Math.floor((totalMinor * cashback.percent) / 100);
  const amountMinor = cashback.capMinor > 0 ? Math.min(raw, cashback.capMinor) : raw;
  return amountMinor > 0 ? { percent: cashback.percent, amountMinor } : null;
}
